/**
 * The pane's chat provider: runtime, question interrupts and thread metadata.
 *
 * A cut-down copy of the web app's `chat-provider.tsx`. It keeps the parts
 * every surface needs (the assistant-ui runtime, the question-response
 * controller whose answers ride on the next message, feedback persisted into
 * thread metadata, the current thread's metadata) and leaves out the web
 * app's tool UIs for editors the pane does not have.
 */
import { AssistantRuntimeProvider, type FeedbackAdapter } from "@assistant-ui/react";
import type { LangChainMessage } from "@assistant-ui/react-langgraph";
import {
  type FC,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";

import { asMetadata, withAgentScope } from "@/agents-v2/chat-runtime/thread-metadata";
import { threadStateCache } from "@/agents-v2/chat-runtime/thread-state-cache";
import type {
  AgentThreadMetadata,
  AgentsChatContextValue,
  ChatProviderProps,
  ThreadMetadataPatch,
  ThreadMetadataPatcher
} from "@/agents-v2/chat-runtime/types";
import { useChatRuntime } from "@/agents-v2/chat-runtime/use-chat-runtime";
import {
  QuestionResponseProvider,
  useQuestionResponseController
} from "@/agents-v2/questions/question-response-context";

const AgentsChatContext = createContext<AgentsChatContextValue | null>(null);

const resolveMetadataPatch = (
  baseMetadata: AgentThreadMetadata,
  patch: ThreadMetadataPatch
): AgentThreadMetadata =>
  typeof patch === "function" ? patch(baseMetadata) : { ...baseMetadata, ...patch };

export type PaneChatProviderProps = ChatProviderProps & {
  apiUrl: string;
  uploadFiles?: Parameters<typeof useChatRuntime>[0]["uploadFiles"];
  /** Called on a 401 the runtime could not recover from. */
  onSessionExpired?: () => void;
  section?: string;
};

export const ChatProvider: FC<PaneChatProviderProps> = ({
  assistantId,
  apiUrl,
  token,
  attachmentAdapter,
  resolveContext,
  onBeforeSend,
  onStreamComplete,
  initialMetadata,
  threadScopeMetadata,
  prepareMessages,
  onSessionExpired,
  section,
  uploadFiles,
  children
}) => {
  const [currentThreadId, setCurrentThreadId] = useState<string | null>(null);
  const questionResponses = useQuestionResponseController();
  const questionPrepareRef = useRef(questionResponses.prepareMessages);
  questionPrepareRef.current = questionResponses.prepareMessages;
  const prepareMessagesRef = useRef(prepareMessages);
  prepareMessagesRef.current = prepareMessages;

  // Question answers ride on the next human message as metadata; the
  // caller's own preparation runs after so it sees the final message list.
  const prepareRuntimeMessages = useCallback(
    (threadId: string, messages: LangChainMessage[]) => {
      const withAnswers = questionPrepareRef.current(threadId, messages);
      return prepareMessagesRef.current?.(threadId, withAnswers) ?? withAnswers;
    },
    []
  );

  const patchThreadMetadataRef = useRef<ThreadMetadataPatcher>(async () => ({}));
  const patchThreadMetadata = useCallback<ThreadMetadataPatcher>(
    (threadId, patch) => patchThreadMetadataRef.current(threadId, patch),
    []
  );

  const feedbackSubmitRef = useRef<FeedbackAdapter["submit"]>(() => undefined);
  const feedbackAdapter = useMemo<FeedbackAdapter>(
    () => ({ submit: (feedback) => feedbackSubmitRef.current(feedback) }),
    []
  );
  // The backend names a thread after its first run ends, a moment after the
  // stream closes. Re-read the metadata a few times so the title bar picks
  // the name up without a reload (and so any other metadata written by the
  // run lands too). The refs are filled in below, once the runtime exists.
  const getActiveThreadIdRef = useRef<() => string | null>(() => null);
  const refreshCurrentThreadRef = useRef<() => Promise<void>>(async () => undefined);
  const titleRefreshTimersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const onStreamCompleteWithTitle = useCallback(async () => {
    await onStreamComplete?.();
    for (const timer of titleRefreshTimersRef.current) clearTimeout(timer);
    titleRefreshTimersRef.current = [1500, 4000, 9000].map((delay) =>
      setTimeout(() => {
        const threadId = getActiveThreadIdRef.current();
        if (threadId) threadStateCache.invalidate(threadId);
        void refreshCurrentThreadRef.current();
      }, delay)
    );
  }, [onStreamComplete]);
  useEffect(() => () => titleRefreshTimersRef.current.forEach(clearTimeout), []);


  const {
    runtime,
    client,
    cancelActiveRun,
    corruptedThreadId,
    clearCorruptedThread,
    sessionExpired,
    isContextCompacting,
    compactionByThread
  } = useChatRuntime({
    assistantId,
    apiUrl,
    token,
    attachmentAdapter,
    feedbackAdapter,
    resolveContext,
    onBeforeSend,
    onStreamComplete: onStreamCompleteWithTitle,
    initialMetadata,
    patchThreadMetadata,
    threadScopeMetadata,
    prepareMessages: prepareRuntimeMessages,
    section,
    uploadFiles
  });

  useEffect(() => {
    if (sessionExpired) onSessionExpired?.();
  }, [sessionExpired, onSessionExpired]);

  const [currentMetadata, setCurrentMetadata] = useState<AgentThreadMetadata>({});
  const currentMetadataRef = useRef<AgentThreadMetadata>({});
  const currentThreadIdRef = useRef<string | null>(null);
  const observedThreadIdRef = useRef<string | null | undefined>(undefined);

  const getActiveThreadId = useCallback(
    () => runtime.threads.mainItem.getState()?.externalId ?? null,
    [runtime]
  );

  const refreshCurrentThread = useCallback(async () => {
    const observedThreadId = getActiveThreadId();
    currentThreadIdRef.current = observedThreadId;
    setCurrentThreadId(observedThreadId);
    if (!observedThreadId) {
      currentMetadataRef.current = {};
      setCurrentMetadata({});
      return;
    }
    const thread = await threadStateCache.getThreadRecord(client, observedThreadId);
    if (currentThreadIdRef.current !== observedThreadId) return;
    const metadata = asMetadata(thread.metadata);
    currentMetadataRef.current = metadata;
    setCurrentMetadata(metadata);
  }, [client, getActiveThreadId]);

  getActiveThreadIdRef.current = getActiveThreadId;
  refreshCurrentThreadRef.current = refreshCurrentThread;

  useEffect(() => {
    const tick = () => {
      const observedThreadId = getActiveThreadId();
      if (observedThreadId === observedThreadIdRef.current) return;
      observedThreadIdRef.current = observedThreadId;
      void refreshCurrentThread();
    };
    const unsubMain = runtime.threads.main.subscribe(tick);
    const unsubList = runtime.threads.subscribe(tick);
    tick();
    return () => {
      unsubMain();
      unsubList();
    };
  }, [getActiveThreadId, runtime, refreshCurrentThread]);

  patchThreadMetadataRef.current = async (threadId, patch) => {
    // Always read the thread first: the cached metadata of the active thread
    // can lag (a run just changed it, or the thread only just became active),
    // and sending it back whole would overwrite keys written since.
    const baseMetadata = asMetadata((await client.threads.get(threadId)).metadata);
    const metadata = withAgentScope(assistantId, resolveMetadataPatch(baseMetadata, patch));
    const updated = await client.threads.update(threadId, { metadata });
    threadStateCache.noteThreadRecord(threadId, updated);
    if (getActiveThreadId() === threadId) {
      currentThreadIdRef.current = threadId;
      setCurrentThreadId(threadId);
      currentMetadataRef.current = metadata;
      setCurrentMetadata(metadata);
    }
    return metadata;
  };

  const updateCurrentThreadMetadata = useCallback(
    async (patch: ThreadMetadataPatch) => {
      const threadId = getActiveThreadId();
      if (!threadId) return;
      await patchThreadMetadata(threadId, patch);
    },
    [getActiveThreadId, patchThreadMetadata]
  );

  feedbackSubmitRef.current = ({ message, type }) => {
    void updateCurrentThreadMetadata((metadata) => ({
      ...metadata,
      feedback: {
        ...(metadata.feedback ?? {}),
        [message.id]: { type, updated_at: new Date().toISOString() }
      }
    })).catch((error) => {
      console.warn("Failed to persist message feedback", error);
    });
  };

  const contextCompaction = currentThreadId
    ? (compactionByThread[currentThreadId] ?? null)
    : null;

  const value = useMemo<AgentsChatContextValue>(
    () => ({
      client,
      currentThreadId,
      currentMetadata,
      refreshCurrentThread,
      updateCurrentThreadMetadata,
      cancelActiveRun,
      corruptedThreadId,
      clearCorruptedThread,
      sessionExpired,
      isContextCompacting,
      contextCompaction
    }),
    [
      client,
      currentThreadId,
      currentMetadata,
      refreshCurrentThread,
      updateCurrentThreadMetadata,
      cancelActiveRun,
      corruptedThreadId,
      clearCorruptedThread,
      sessionExpired,
      isContextCompacting,
      contextCompaction
    ]
  );

  return (
    <AgentsChatContext.Provider value={value}>
      <QuestionResponseProvider value={questionResponses}>
        <AssistantRuntimeProvider runtime={runtime}>{children}</AssistantRuntimeProvider>
      </QuestionResponseProvider>
    </AgentsChatContext.Provider>
  );
};

export const useAgentsChat = () => {
  const context = useContext(AgentsChatContext);
  if (!context) {
    throw new Error("useAgentsChat must be used inside ChatProvider.");
  }
  return context;
};
